import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import api from '../../services/api';

const CELL_COLORS = {
  booth: '#1976d2', // Blue for available booths
  booth_registered: '#424242', // Dark grey for registered booths
  booth_unavailable: '#9e9e9e', // Light grey for unavailable booths
  booth_my_registration: '#1976d2', // Blue for vendor's own registration
  booth_selected: '#2e7d32', // Green for selected booths
  walkway: '#bdbdbd',
  food: '#ffb300',
  empty: '#fff',
  selected: '#1976d2', // Blue for selected booth
};

const BASE_CELL_SIZE = 30;

const BoothSelectionMap = ({ onSelect, initialMapData, selectedBooths = [] }) => {
  const [cells, setCells] = useState(initialMapData || []);
  const [loading, setLoading] = useState(!initialMapData);
  const [error, setError] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [myBooth, setMyBooth] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        if (!initialMapData) {
          console.log('No initial map data, fetching from API...');
          const { data: mapData } = await api.get('/map');
          console.log('Received map data:', {
            length: mapData?.length,
            firstFewItems: mapData?.slice(0, 3),
            data: mapData
          });
          setCells(mapData);
        } else {
          console.log('Using initial map data:', {
            length: initialMapData?.length,
            firstFewItems: initialMapData?.slice(0, 3)
          });
          setCells(initialMapData);
        }

        const { data: boothData } = await api.get('/vendor/booth-registration');
        console.log('Received booth registration data:', {
          length: boothData?.length,
          data: boothData
        });
        if (boothData.length > 0) {
          setMyBooth(boothData[0]);
        }
      } catch (err) {
        console.error('Error loading map data:', {
          error: err,
          response: err.response,
          message: err.message
        });
        setError(err.response?.data?.error || 'Failed to load map data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [initialMapData]);

  // Find grid size
  const width = Math.max(...cells.map(c => c.x_coordinate), 0);
  const height = Math.max(...cells.map(c => c.y_coordinate), 0);

  console.log('Grid dimensions:', { width, height, cellCount: cells.length });

  // Build grid
  const grid = Array.from({ length: height }, (_, y) =>
    Array.from({ length: width }, (_, x) =>
      cells.find(c => c.x_coordinate === x + 1 && c.y_coordinate === y + 1)
    )
  );

  console.log('Built grid:', {
    rows: grid.length,
    columns: grid[0]?.length,
    totalCells: grid.flat().length,
    filledCells: grid.flat().filter(Boolean).length
  });

  const handleCellClick = (cell) => {
    if (cell?.cell_type === 'booth') {
      if (!cell.is_registered && cell.booth_status !== 'unavailable') {
        setSelectedCell(cell);
        if (onSelect) {
          onSelect(cell);
        }
      }
    }
  };

  const getCellColor = (cell) => {
    if (!cell) return CELL_COLORS.empty;
    if (cell.cell_type === 'booth') {
      if (selectedBooths.some(booth => booth.id === cell.id)) {
        return CELL_COLORS.booth_selected;
      }
      if (myBooth && cell.id === myBooth.booth_id) return CELL_COLORS.booth_my_registration;
      if (cell.is_registered) return CELL_COLORS.booth_registered;
      if (cell.booth_status === 'unavailable') return CELL_COLORS.booth_unavailable;
      if (cell === selectedCell) return CELL_COLORS.selected;
      return CELL_COLORS.booth;
    }
    return CELL_COLORS[cell.cell_type] || CELL_COLORS.empty;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box 
        sx={{ 
          background: '#f5f6fa', 
          mx: 'auto', 
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${width}, ${BASE_CELL_SIZE}px)`,
            gridTemplateRows: `repeat(${height}, ${BASE_CELL_SIZE}px)`,
            gap: '1px',
            background: '#888',
            justifyContent: 'center',
            userSelect: 'none',
          }}
        >
          {grid.flat().map((cell, i) => (
            <Box
              key={i}
              sx={{
                width: BASE_CELL_SIZE,
                height: BASE_CELL_SIZE,
                background: getCellColor(cell),
                boxSizing: 'border-box',
                cursor: cell?.cell_type === 'booth' && !cell?.is_registered ? 'pointer' : 'default',
                transition: 'box-shadow 0.2s, border 0.2s',
                border: cell === selectedCell ? '2px solid #1976d2' : 'none',
                position: 'relative',
                '&:hover': {
                  boxShadow: cell?.cell_type === 'booth' && !cell?.is_registered ? '0 0 0 2px #1976d2' : 'none',
                  zIndex: 1,
                },
              }}
              onClick={() => handleCellClick(cell)}
              title={cell?.cell_name || cell?.cell_type || 'empty'}
            >
              {cell?.cell_name && cell?.cell_type === 'booth' && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '12px',
                    lineHeight: 1,
                    color: '#fff',
                    textAlign: 'center',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}
                >
                  {cell.cell_name}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default BoothSelectionMap; 